import includes from 'lodash/includes';
import {getRootStore} from '../app.bootstrap';
import {RouteName} from '../router/route-names';
import {UserKeyTypes} from '@techsee/techsee-common/lib/constants/copilot.constants';
import {UserType} from '@techsee/techsee-common/lib/constants/room.constants';
import {SdkToHostNotifications} from '@techsee/sophie-shared-contracts/lib/assistant/constants/sdk-to-host-notifications';

function getCopilotUrl(): string {
    return includes(window.location.origin as any, 'localhost') ? 'http://localhost:5021' : window.location.origin;
}

function loadScript(url: string) {
    return new Promise((resolve, reject) => {
        const scriptElement = document.createElement('script');

        scriptElement.src = url;
        scriptElement.onload = resolve;
        scriptElement.onerror = reject;
        document.body.appendChild(scriptElement);
    });
}

const fullHeight = '90vh';
const sourceApp = 'Techsee-Live';

export interface ICopilotService {
    init(copilotElement: HTMLDivElement): Promise<void>;
    imageCaptured(url: string, base64: string, sender: string, isSnapshot?: boolean): void;
    endMeeting(roomId: string): void;
    startMeeting(roomId: string): void;
    cameraPermissionSet(isCameraOn: boolean): void;
    reloadSession(): void;
}

export class CopilotService implements ICopilotService {
    private copilot: any;
    private copilotSdk: any;
    private plugin: any;
    private readonly version: string;
    private readonly accountId: string;
    private readonly userId: string;
    private readonly isHosting: boolean;
    private readonly jsApiService: any;
    private readonly sophieContext?: string;
    private readonly analyzedImagesByCustomerOnly: boolean;
    private readonly stateHelper: any;

    constructor(copilot: any, accountId: string, userId: string, stateHelper: any, sophieContext?: string) {
        this.version = copilot.version;
        this.analyzedImagesByCustomerOnly = copilot.analyzedImagesByCustomerOnly;
        this.isHosting = window.self === window.top;
        this.accountId = accountId;
        this.userId = userId;
        this.sophieContext = sophieContext;
        this.jsApiService = getRootStore().jsApiService;
        this.stateHelper = stateHelper;
    }

    async loadSdkAndPlugin(isCameraOn: boolean = false): Promise<void> {
        const scriptUrl = includes(origin, 'localhost')
            ? `/js/copilot-sdk.${this.version}.min.js`
            : `${origin}/sophie/assistant/sdk/js/copilot-sdk.${this.version}.min.js`;

        await loadScript(scriptUrl);
        // @ts-ignore
        const copilotSdk = window.Copilot;

        this.copilotSdk = copilotSdk;

        const pluginConfig = {
            transportHub: this.isHosting
                ? this.copilotSdk.TransportHub.DirectMessageHub
                : this.copilotSdk.TransportHub.PostMessageHub,
            sourceApp: sourceApp
        };

        this.plugin = new this.copilotSdk.Plugin(pluginConfig);

        this.jsApiService.sophieAssistantReady();

        if (isCameraOn) {
            this.cameraPermissionSet(isCameraOn);
        }
    }

    reloadSession(): void {
        const message = {
            action: 'sessionTokenRefreshed',
            data: null
        };
        // @ts-ignore

        this.plugin.sendMessage(message);
    }

    async init(copilotElement: HTMLDivElement): Promise<void> {
        await this.loadSdkAndPlugin();

        if (this.isHosting) {
            await this.initCopilotApp(copilotElement);
        }
    }

    async initCopilotApp(copilotElement: HTMLDivElement) {
        const copilotSdkSettings = {
            copilotUrl: getCopilotUrl(),
            pluginTransports: [this.copilotSdk.TransportHub.DirectMessageHub],
            pluginOutputDestination: window,
            meta: {
                userId: this.userId,
                accountId: this.accountId,
                sophieContext: this.sophieContext,
                userKeyType: UserKeyTypes.Internal,
                appLanguage: getRootStore().tsLocaleHelper.getLocale()
            }
        };
        const sdkConfig = {
            container: copilotElement,
            appearance: {
                height: fullHeight
            }
        };

        this.copilot = new this.copilotSdk.CopilotSdk(copilotSdkSettings);
        this.copilot.on(SdkToHostNotifications.sessionTokenExpired, (data: any) => {
            void this.stateHelper.navigateTo(RouteName.LOGIN);
        });

        await this.copilot.init(sdkConfig);
    }

    startMeeting(externalSessionId: string) {
        const sessionExternalStarted = 'sessionExternalStartedSent';

        const message = {
            action: sessionExternalStarted,
            data: {externalSessionId}
        };

        // @ts-ignore
        this.plugin.sendMessage(message);
    }

    imageCaptured(url: string, base64: string, sender: string, isSnapshot?: boolean): void {
        const imageCaptured = 'imageCaptureSent';
        const isImageForAnalyze = sender == UserType.client || isSnapshot;
        const shouldAnalyzeImage = this.analyzedImagesByCustomerOnly ? isImageForAnalyze : true;

        const message = {
            action: imageCaptured,
            data: {base64, url}
        };

        // @ts-ignore
        if (shouldAnalyzeImage) {
            this.plugin.sendMessage(message);
        }
    }

    endMeeting(externalSessionId: string): void {
        if (!this.plugin) {
            return;
        }

        const sessionExternalEnded = 'sessionExternalEndedSent';

        const message = {
            action: sessionExternalEnded,
            data: {externalSessionId: externalSessionId}
        };

        // @ts-ignore
        this.plugin.sendMessage(message);
    }

    cameraPermissionSet(isCameraOn: boolean): void {
        // @ts-ignore
        const cameraPermission = 'cameraPermissionSent';
        const message = {
            action: cameraPermission,
            data: isCameraOn
        };

        // @ts-ignore
        this.plugin.sendMessage(message);
    }
}
